import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { PageControl } from '../model/page-control';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import {
	ClickedLink,
	ClickedLinkPromotedContent,
	ContainerCardsHighlights,
	DashboardDemographicHistoryItem,
	DashboardDemographicType,
	DashboardEngagement,
	DashboardEntityType,
	DashboardFilter,
	DashboardFilterOptions,
	DashboardFilterPageControl,
	DashboardGrouping,
	DashboardLinkTypeItem,
	DashboardPromotedContentsSubItem,
	DashboardPromotedContentTypeItem,
	DashboardPromotedContentUrlClicks,
	ReportOverviewRequestHelper,
	ReportRealEstateRequestHelper,
	ReportTicketResponse,
	DashboardStatistics,
	DashboardStatType,
	DashboardSummary,
	DashboardRealEstateOverview,
	DashboardOverview,
	DashboardTimeSeries,
	DashboardTimeSeriesItem,
	DashboardTopBottomGrouping,
	PageInfoHelperDashboardCardPopularityItem,
	PageInfoHelperDashboardDemographicItem,
	PageInfoHelperDashboardLinkTypeItem,
	PageInfoHelperDashboardPopularOwnerItem,
	PageInfoHelperDashboardPopularPropertyItem,
	PageInfoHelperDashboardPromotedContentItem,
	PageInfoHelperDashboardPromotedContents,
	PageInfoHelperDashboardShowcaseAnalyticsItem,
	PostData
} from '@app-model/dashboard';
import { CardholderListItem } from '@app-model/cardholder';

@Injectable({
	providedIn: 'root'
})
export class DashboardService {
	constructor(private http: HttpClient) {}

	getSummary(filter: DashboardFilter): Observable<DashboardSummary> {
		return this.http.post<DashboardSummary>(`${environment.api}/dashboard/summary`, filter);
	}

	getOverview(filter: DashboardFilter): Observable<DashboardOverview> {
		return this.http.post<DashboardOverview>(`${environment.api}/dashboard/overview`, filter);
	}

	getTimeSeries(
		filter: DashboardFilter,
		stat: DashboardStatType,
		grouping: DashboardGrouping
	): Observable<DashboardTimeSeriesItem[]> {
		return this.http.post<DashboardTimeSeriesItem[]>(
			`${environment.api}/dashboard/timeSeries/${stat}/${grouping}`,
			filter
		);
	}

	getTopBottomCards(
		filter: DashboardFilter,
		stat: DashboardStatType,
		grouping: DashboardTopBottomGrouping,
		limit?: number
	): Observable<CardholderListItem[]> {
		const url = limit
			? `${environment.api}/dashboard/topBottomCards/${stat}/${grouping}/${limit}`
			: `${environment.api}/dashboard/topBottomCards/${stat}/${grouping}`;
		return this.http.post<CardholderListItem[]>(url, filter);
	}

	getEngagement(filter: DashboardFilter): Observable<DashboardEngagement> {
		return this.http.post<DashboardEngagement>(`${environment.api}/dashboard/engagement`, filter);
	}

	getFilterOptions(): Observable<DashboardFilterOptions> {
		return this.http.get<DashboardFilterOptions>(`${environment.api}/dashboard/filterOptions`);
	}

	getMostClickedLinks(filter: DashboardFilter): Observable<DashboardLinkTypeItem[]> {
		return this.http.post<DashboardLinkTypeItem[]>(`${environment.api}/dashboard/mostClickedLinks`, filter);
	}

	getMostClickedContactTypes(
		filterPageControl: DashboardFilterPageControl
	): Observable<PageInfoHelperDashboardLinkTypeItem> {
		return this.http.post<PageInfoHelperDashboardLinkTypeItem>(
			`${environment.api}/dashboard/mostClickedContactTypes`,
			filterPageControl
		);
	}

	getMostClickedPromotedContentTypes(filter: DashboardFilter): Observable<DashboardPromotedContentTypeItem[]> {
		return this.http.post<DashboardPromotedContentTypeItem[]>(
			`${environment.api}/dashboard/mostClickedPromotedContentTypes`,
			filter
		);
	}

	getMostClickedPromotedContents(
		filterPageControl: DashboardFilterPageControl
	): Observable<PageInfoHelperDashboardPromotedContentItem> {
		return this.http.post<PageInfoHelperDashboardPromotedContentItem>(
			`${environment.api}/dashboard/mostClickedPromotedContents`,
			filterPageControl
		);
	}

	getPromotedContentStats(
		filter: DashboardFilter,
		clientId: string,
		elementId: string
	): Observable<DashboardPromotedContentUrlClicks[]> {
		return this.http.post<DashboardPromotedContentUrlClicks[]>(
			`${environment.api}/dashboard/promotedContentClicksByUrl/${clientId}/${elementId}`,
			filter
		);
	}

	getPromotedContents(
		filterPageControl: DashboardFilterPageControl
	): Observable<PageInfoHelperDashboardPromotedContents> {
		return this.http.post<PageInfoHelperDashboardPromotedContents>(
			`${environment.api}/dashboard/promotedContents`,
			filterPageControl
		);
	}

	getPromotedContentById(
		filter: DashboardFilter,
		promotedContentId: string
	): Observable<DashboardPromotedContentsSubItem[]> {
		return this.http.post<DashboardPromotedContentsSubItem[]>(
			`${environment.api}/dashboard/promotedContents/${promotedContentId}`,
			filter
		);
	}

	getCardPopularity(
		filterPageControl: DashboardFilterPageControl
	): Observable<PageInfoHelperDashboardCardPopularityItem> {
		return this.http.post<PageInfoHelperDashboardCardPopularityItem>(
			`${environment.api}/dashboard/cardPopularity`,
			filterPageControl
		);
	}

	getPaginatedDemographics(
		filterPageControl: DashboardFilterPageControl,
		type: DashboardDemographicType
	): Observable<PageInfoHelperDashboardDemographicItem> {
		return this.http.post<PageInfoHelperDashboardDemographicItem>(
			`${environment.api}/dashboard/demographics/${type}`,
			filterPageControl
		);
	}

	getDemographicsHistory(
		filter: DashboardFilter,
		type: DashboardDemographicType
	): Observable<DashboardDemographicHistoryItem[]> {
		return this.http.post<DashboardDemographicHistoryItem[]>(
			`${environment.api}/dashboard/demographics/history/${type}`,
			filter
		);
	}

	getMostPopularPropertiesList(
		filterPageControl: DashboardFilterPageControl
	): Observable<PageInfoHelperDashboardPopularPropertyItem> {
		return this.http.post<PageInfoHelperDashboardPopularPropertyItem>(
			`${environment.api}/dashboard/mostPopularProperties`,
			filterPageControl
		);
	}

	getMostPopularPropertiesListByEntity(
		filterPageControl: DashboardFilterPageControl,
		entityType: DashboardEntityType,
		entityId: string
	): Observable<PageInfoHelperDashboardPopularPropertyItem> {
		return this.http.post<PageInfoHelperDashboardPopularPropertyItem>(
			`${environment.api}/dashboard/mostPopularProperties/${entityType}/${entityId}`,
			filterPageControl
		);
	}

	getMostPopularOwners(
		filterPageControl: DashboardFilterPageControl
	): Observable<PageInfoHelperDashboardPopularOwnerItem> {
		return this.http.post<PageInfoHelperDashboardPopularOwnerItem>(
			`${environment.api}/dashboard/mostPopularProperties/owners`,
			filterPageControl
		);
	}

	getRealEstateSummary(filter: DashboardFilter): Observable<DashboardRealEstateOverview> {
		return this.http.post<DashboardRealEstateOverview>(`${environment.api}/dashboard/realEstate/summary`, filter);
	}

	getRealEstateTimeSeries(
		filter: DashboardFilter,
		stat: DashboardStatType,
		grouping: DashboardGrouping
	): Observable<DashboardTimeSeriesItem[]> {
		return this.http.post<DashboardTimeSeriesItem[]>(
			`${environment.api}/dashboard/realEstate/timeSeries/${stat}/${grouping}`,
			filter
		);
	}

	getShowcasesList(
		filterPageControl: DashboardFilterPageControl
	): Observable<PageInfoHelperDashboardShowcaseAnalyticsItem> {
		return this.http.post<PageInfoHelperDashboardShowcaseAnalyticsItem>(
			`${environment.api}/dashboard/showcases/list`,
			filterPageControl
		);
	}

	getShowcaseCardholders(
		filterPageControl: DashboardFilterPageControl,
		showcaseId: string
	): Observable<PageInfoHelperDashboardCardPopularityItem> {
		return this.http.post<PageInfoHelperDashboardCardPopularityItem>(
			`${environment.api}/dashboard/showcases/${showcaseId}/cardholders`,
			filterPageControl
		);
	}

	getShowcaseContents(
		filterPageControl: DashboardFilterPageControl,
		showcaseId: string
	): Observable<PageInfoHelperDashboardPromotedContents> {
		return this.http.post<PageInfoHelperDashboardPromotedContents>(
			`${environment.api}/dashboard/showcases/${showcaseId}/contents`,
			filterPageControl
		);
	}

	getShowcaseContentsById(
		filter: DashboardFilter,
		showcaseId: string,
		promotedContentId: string
	): Observable<DashboardPromotedContentsSubItem[]> {
		return this.http.post<DashboardPromotedContentsSubItem[]>(
			`${environment.api}/dashboard/showcases/${showcaseId}/contents/${promotedContentId}`,
			filter
		);
	}

	/** @deprecated Use getSummary() */
	dashboardSummary(dashboardData: PostData): Observable<DashboardStatistics> {
		return this.http.post<DashboardStatistics>(`${environment.api}/dashboard/summary`, dashboardData);
	}

	/** @deprecated Use getTimeSeries() */
	dashboardTimeSeries(dashboardData: PostData, grouping: string, sort: string): Observable<DashboardTimeSeries[]> {
		return this.http.post<DashboardTimeSeries[]>(
			`${environment.api}/dashboard/timeSeries/${grouping}/${sort}`,
			dashboardData
		);
	}

	/** @deprecated Use getTopBottomCards() */
	dashboardTimeTopBottom(
		dashboardData: PostData,
		grouping: string,
		sort: string,
		limit: string
	): Observable<CardholderListItem[]> {
		const limitPart = limit !== 'ALL' ? `/${limit}` : '';
		return this.http.post<CardholderListItem[]>(
			`${environment.api}/dashboard/topBottomCards/${grouping}/${sort}${limitPart}`,
			dashboardData
		);
	}

	/** @deprecated Use getMostClickedLinks() */
	mostClickedLinks(dashboardData: PostData): Observable<ClickedLink[]> {
		return this.http.post<ClickedLink[]>(`${environment.api}/dashboard/mostClickedLinks`, dashboardData);
	}

	/** @deprecated Use getMostClickedPromotedContentTypes() */
	mostClickedPromotedContentTypes(dashboardData: PostData): Observable<ClickedLinkPromotedContent[]> {
		return this.http.post<ClickedLinkPromotedContent[]>(
			`${environment.api}/dashboard/mostClickedPromotedContentTypes`,
			dashboardData
		);
	}

	/** @deprecated Use getMostClickedPromotedContents() */
	clickedHighlightsTable(dashboardData: PostData, pageControl: PageControl): Observable<ContainerCardsHighlights> {
		const bodyPageControl: any = {
			page: pageControl.page,
			pageSize: 10
		};

		const body = {
			filter: dashboardData,
			pageControl: bodyPageControl
		};

		return this.http.post<ContainerCardsHighlights>(`${environment.api}/dashboard/mostClickedPromotedContents`, body);
	}

	/** @deprecated Use getPromotedContentStats() */
	dashboardPromotedContentClicksByUrl(dashboardData: PostData, clientId: string, elementId: string): Observable<any[]> {
		return this.http.post<any[]>(
			`${environment.api}/dashboard/promotedContentClicksByUrl/${clientId}/${elementId}`,
			dashboardData
		);
	}

	downloadGeneralCSV(filter: DashboardFilter): Observable<any> {
		return this.http.post(`${environment.api}/dashboard/downloadGeneralCSV`, filter, {
			headers: new HttpHeaders({
				Accept: 'application/csv'
			}),
			responseType: 'blob' as 'json'
		});
	}

	downloadContentsCSV(filter: DashboardFilter): Observable<any> {
		return this.http.post(`${environment.api}/dashboard/downloadContentsCSV`, filter, {
			headers: new HttpHeaders({
				Accept: 'application/csv'
			}),
			responseType: 'blob' as 'json'
		});
	}

	downloadShowcasesCSV(filter: DashboardFilter): Observable<Blob> {
		return this.http.post(`${environment.api}/dashboard/downloadShowcasesCSV`, filter, {
			headers: new HttpHeaders({
				Accept: 'application/csv'
			}),
			responseType: 'blob' as 'blob'
		});
	}

	downloadRealEstateCSV(filter: DashboardFilter): Observable<Blob> {
		return this.http.post(`${environment.api}/dashboard/downloadRealEstateCSV`, filter, {
			headers: new HttpHeaders({
				Accept: 'application/csv'
			}),
			responseType: 'blob' as 'blob'
		});
	}

	generateOverviewPdf(payload: ReportOverviewRequestHelper): Observable<HttpResponse<Blob>> {
		return this.http.post(`${environment.api}/reports/generate/overview`, payload, {
			headers: new HttpHeaders({
				Accept: 'application/pdf'
			}),
			observe: 'response',
			responseType: 'blob'
		});
	}

	generateOverviewPdfDebugTicket(payload: ReportOverviewRequestHelper): Observable<HttpResponse<Blob>> {
		return this.http.post(`${environment.api}/reports/generate/debugTicket`, payload, {
			headers: new HttpHeaders({
				Accept: 'application/pdf'
			}),
			observe: 'response',
			responseType: 'blob'
		});
	}

	generateRealEstatePdf(payload: ReportRealEstateRequestHelper): Observable<HttpResponse<Blob>> {
		return this.http.post(`${environment.api}/reports/generate/realEstate`, payload, {
			headers: new HttpHeaders({
				Accept: 'application/pdf'
			}),
			observe: 'response',
			responseType: 'blob'
		});
	}

	getReportTicket(ticketId: string): Observable<ReportTicketResponse> {
		const encodedTicketId = encodeURIComponent(ticketId);
		return this.http.get<ReportTicketResponse>(`${environment.api}/reports/ticket/${encodedTicketId}`);
	}
}
